import React, { useEffect, useState, useCallback } from 'react';
import { Flex } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { SbButton, SbModal, SbInput, SbTooltip, TooltipTriggerStyled } from 'uiCore';
import Permission from 'Components/Permission';
import PageHeader from 'Components/PageHeader';
import useProfiles from './useProfiles';
import ProfileTableV2 from './ProfileTableV2';
import ProfileDrawerV2 from './ProfileDrawerV2';
import {
  ProfilePageWrapperStyled,
  ProfileTableWrapperStyled,
} from './ProfileManagementV2.styles';


const TOOLTIP_CREATE_PROFILE = "Create a new access profile with a custom set of module permissions.";


const ProfileManagementV2 = () => {
  const { profiles, loading, fetchProfiles, createProfile } = useProfiles();

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [selectedFeatureName, setSelectedFeatureName] = useState('');

  // Create profile modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const handleFeatureClick = useCallback(({ profile, featureId, featureName }) => {
    setSelectedProfile(profile);
    setSelectedFeatureId(featureId);
    setSelectedFeatureName(featureName);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedProfile(null);
    setSelectedFeatureId(null);
    setSelectedFeatureName('');
  }, []);

  const handleDrawerSaveSuccess = useCallback(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleCreateProfile = useCallback(async () => {
    if (!newProfileName.trim()) return;
    setCreating(true);
    const success = await createProfile(newProfileName.trim());
    setCreating(false);
    if (success) {
      setCreateModalOpen(false);
      setNewProfileName('');
    }
  }, [newProfileName, createProfile]);

  const handleCreateModalCancel = useCallback(() => {
    setCreateModalOpen(false);
    setNewProfileName('');
  }, []);

  return (
    <ProfilePageWrapperStyled>
      <PageHeader
        title="Manage Profiles"
        actions={
          <Permission resource="profile" actions={['create']}>
            <SbTooltip content={TOOLTIP_CREATE_PROFILE}>
              <TooltipTriggerStyled>
                <SbButton
                  buttonType="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalOpen(true)}
                >
                  Create New Profile
                </SbButton>
              </TooltipTriggerStyled>
            </SbTooltip>
          </Permission>
        }
      />

      <ProfileTableWrapperStyled>
        <ProfileTableV2
          profiles={profiles}
          loading={loading}
          onFeatureClick={handleFeatureClick}
        />
      </ProfileTableWrapperStyled>

      <ProfileDrawerV2
        open={drawerOpen}
        profile={selectedProfile}
        featureId={selectedFeatureId}
        featureName={selectedFeatureName}
        profiles={profiles}
        onClose={handleDrawerClose}
        onSaveSuccess={handleDrawerSaveSuccess}
      />

      <SbModal
        open={createModalOpen}
        title="Create New Profile"
        onOk={handleCreateProfile}
        onCancel={handleCreateModalCancel}
        confirmLoading={creating}
        okText="Create"
      >
        <SbInput
          placeholder="Enter profile name"
          value={newProfileName}
          onChange={(e) => setNewProfileName(e.target.value)}
          onPressEnter={handleCreateProfile}
        />
      </SbModal>
    </ProfilePageWrapperStyled>
  );
};

export default ProfileManagementV2;
